/*
 * Replaces SATComponent.prototype.startBoardWave outright — the bug this
 * fixes is in the orchestration itself, so there's nothing left to preserve
 * by wrapping it (same call as assets/sat-popup-dedupe-patch.js).
 *
 * The base version advanced all 6 Departures rows through 5 steps, each step
 * staggered 200ms row-to-row (n*200 = 1200ms for the cascade to reach the
 * last row) — that part reads fine as a top-to-bottom sweep. The problem was
 * everything AFTER the cascade landed: it then held completely still for a
 * separate, much longer wait (n*200+2400 = 3600ms total per step) before the
 * next cascade began, and once every lap (after step 5) it did a special
 * SHORTER pass instead — an instant, invisible snap back to step 0 (step 5
 * and step 0 show the same city, since the stack's last entry repeats its
 * first — see the flipTransform math in sat-app.js) followed by its own
 * separate, shorter wait (n*200+300 = 1500ms). Three different rhythms
 * sharing one loop — a long hold, a short hold, a long hold, a short hold —
 * is what actually read as "clunky" and "held up on the last row or two":
 * the reset lap is a full 2100ms shorter than every other lap, a real
 * hitch in what should be one steady pulse.
 *
 * Fixed by folding the reset into the END of step 5's own hold instead of
 * giving it a separate timed beat: the snap back to 0 happens instantly,
 * with no wait of its own, right before the loop's next lap starts step 1's
 * cascade. Every one of the 5 real, visible holds is now exactly the same
 * length — a single steady rhythm.
 *
 * Also swaps the per-row flip's easing from a bouncy overshoot
 * (cubic-bezier(.3,1.4,.6,1), where the 1.4 makes each flap spring past its
 * resting point and settle back) for a plain smooth ease — "clunky" was a
 * literal description of that overshoot, not just the timing gaps above.
 *
 * ---- The live 7th row rides this SAME loop now, not one of its own ----
 * assets/sat-live-orders-patch.js used to run the live row (real customer
 * orders, appended below the 6 fixed rows) on its own independent
 * setTimeout loop, sized to match this one's cadence as closely as
 * possible. Two loops that only *approximate* the same cadence drift out
 * of phase from each other — different start times, and every stall either
 * one hits (a throttled tab, a slow tick, anything) shifts it independently
 * of the other. That's exactly what read as "not synced, some rows too
 * early, others too late": it wasn't one wave, it was two waves that
 * happened to move at close to the same speed.
 *
 * Fixed by making this the single authoritative clock for every row, fixed
 * and live alike: each lap, the live row (when there are any live orders)
 * is scheduled as row index 6 in the exact same stagger sequence as rows
 * 0-5 — one more setTimeout at 6*ROW_STAGGER, same as if it were just
 * another fixed row — and reads its own step from window.SATLiveBoardBridge
 * (assets/sat-live-orders-patch.js), which owns the live-order data this
 * file has no business knowing about. The live row's own stack can be
 * shorter than the fixed rows' (1-5 real orders vs. always 5 cities), so
 * its displayed step wraps modulo its own count instead of reusing the
 * fixed rows' step number directly — but it advances, holds, and resets on
 * the identical tick as everything above it, because there is only one
 * loop now, not two loops keeping time separately.
 *
 * ---- Why the 7th row still sat frozen after all of the above ----
 * componentDidMount calls `this.startBoardWave()` exactly once, and that
 * one call is where the bug actually lived. Every other patch in this
 * theme wraps renderVals, which the runtime looks up fresh from the
 * prototype on every single render — so it never matters exactly when
 * such a patch's script finishes loading relative to first paint, the very
 * next render just picks up whatever's current. startBoardWave is
 * different: it's a long-running loop invoked ONE TIME, and once that one
 * call has started, it's already bound to whichever function body was on
 * the prototype AT THAT INSTANT — reassigning the prototype afterward (as
 * this file's own replacement above does) has no effect on a loop that's
 * already running, the same way replacing a recipe on the wall doesn't
 * change a dish that's already in the oven. Confirmed live: the board's
 * own timing consistently measured out to the ORIGINAL base version's
 * 3600ms-per-step cadence, not this file's 3280-3500ms one, even with
 * every fix above correctly deployed and correctly installed on the
 * prototype — componentDidMount's one call had already locked onto the
 * pre-patch function before this script's replacement ever took effect,
 * and nothing after that point could reach it. The live row never had a
 * chance: SATLiveBoardBridge doesn't exist at all in the version that
 * ended up running.
 *
 * ---- Why forcing a restart made it WORSE, not better ----
 * The first fix for the above forced a takeover by setting
 * this._boardRunning = false (the flag the base setRowStep(i,val,noTrans)
 * guards on: `if(!this._boardRunning || ...) return;`) and then, 50ms
 * later, calling startBoardWave() again, which immediately sets that same
 * flag back to true. That's a brief PULSE, not a kill: the pre-patch
 * loop's own outer while only rechecks the flag once per full lap (up to
 * ~17.5s), so a 50ms dip does nothing to a loop that's mid-lap — and once
 * the flag flips back to true, every setRowStep call the OLD loop's own
 * still-pending timers make from then on passes the guard and succeeds
 * again, completely unaffected. The result was two independent loops
 * (the stale pre-patch one and this file's new one) both alive at once,
 * both calling setRowStep on the exact same shared state on two different
 * schedules — which reads as rows going out of sync with each other, and
 * a scroll-triggered render landing mid-collision could catch the 7th row
 * (or any row) on either loop's half-applied value, looking "stuck."
 *
 * Fixed properly below: this._boardRunning is set false ONCE, at the top
 * of every call to this function, and NEVER set back to true again by
 * anything in this file — so any stale loop gating on that flag (whether
 * it's the original pre-patch version, or an earlier lap of this very
 * function from a previous call) is permanently and unambiguously locked
 * out, forever, the instant a new call starts. This function's OWN row
 * updates don't go through setRowStep at all any more (which is the only
 * thing that flag ever gated) — setBoardRowStep below reimplements the
 * exact same state update inline, gated only by a private generation
 * counter (myGen / current()) that only THIS invocation controls, so nothing
 * about disabling the shared flag can ever block this function's own work.
 * A generation check (not just the flag) also covers the case of two calls
 * to this NEW function racing each other (e.g. the takeover below firing
 * more than once): whichever call is current wins outright, and any
 * earlier one recognizes it's been superseded within at most one step
 * (~3.3s) and exits cleanly instead of continuing to run alongside it.
 */
(function () {
  'use strict';
  if (!window.SATComponent) { return; }
  if (typeof window.SATComponent.prototype.startBoardWave !== 'function') { return; }

  // Merchant's call after seeing it live: a tad slow, on both desktop and
  // mobile. Trimmed each of the three timings by roughly 15-20% rather than
  // just one of them, so the whole cadence — cascade, flip, and hold —
  // reads uniformly quicker instead of only shortening the pause between
  // sweeps (was 220/480/1700, a full step-to-step lap ran ~3.28s at 6 rows;
  // now ~2.82s, noticeably brisker without turning into a blur).
  var ROW_STAGGER = 190; // ms between each row's flip kicking off — the cascade itself
  var FLIP_MS = 420;     // matches the eased transition duration set below
  var HOLD_MS = 1450;    // time to actually read a row's new value once the sweep finishes

  window.SATComponent.prototype.startBoardWave = async function () {
    var self = this;

    // Permanently silences setRowStep for anyone still gating on it — the
    // original pre-patch version of this method, or an earlier call to
    // this very function. Never set back to true by this file; this
    // function's own updates use setBoardRowStep below instead, which
    // doesn't check this flag at all, so turning it off here can never
    // block this call's own work, only a foreign/stale one's.
    this._boardRunning = false;

    var myGen = (this._boardWaveGen = (this._boardWaveGen || 0) + 1);
    function current() { return self._boardWaveGen === myGen; }

    // A private reimplementation of the base setRowStep(i, val, noTrans) —
    // same state shape, same noTrans re-enable dance — gated on this call's
    // own generation instead of the shared (now permanently-false) flag.
    function setBoardRowStep(i, val, noTrans) {
      if (!current() || self.state.view !== 'site') return;
      self.setState(function (s) {
        var step = s.boardStep.slice(); step[i] = val;
        var nt = s.boardNoTransition.slice(); nt[i] = noTrans;
        return { boardStep: step, boardNoTransition: nt };
      });
      if (!noTrans && typeof self.isBoardVisible === 'function' && typeof self.playRollTick === 'function') {
        try { if (self.isBoardVisible()) self.playRollTick(); } catch (e) {}
      }
      if (noTrans) {
        setTimeout(function () {
          if (!current()) return;
          self.setState(function (s) { var nt = s.boardNoTransition.slice(); nt[i] = false; return { boardNoTransition: nt }; });
        }, 70);
      }
    }

    var n = this._boardRows.length; // the 6 fixed rows
    while (current()) {
      // Re-checked once per lap (not once per step): the live row can come
      // and go as real orders arrive, but flipping whether it's part of the
      // sweep mid-lap would itself be a desync — a clean boundary between
      // laps is the only place that's safe to pick up or drop it.
      var bridge = window.SATLiveBoardBridge;
      var liveCount = bridge ? bridge.count() : 0;
      var hasLive = liveCount > 0;
      var totalRows = n + (hasLive ? 1 : 0);
      var sweep = (totalRows - 1) * ROW_STAGGER;

      for (var step = 1; step <= 5 && current(); step++) {
        for (var i = 0; i < n; i++) {
          (function (i) { setTimeout(function () { setBoardRowStep(i, step, false); }, i * ROW_STAGGER); })(i);
        }
        if (hasLive) {
          var liveStep = ((step - 1) % liveCount) + 1;
          setTimeout(function () {
            if (!current()) return;
            bridge.setStep(liveStep, false);
            if (typeof self.forceUpdate === 'function') self.forceUpdate();
            if (typeof self.isBoardVisible === 'function' && typeof self.playRollTick === 'function') {
              try { if (self.isBoardVisible()) self.playRollTick(); } catch (e) {}
            }
          }, n * ROW_STAGGER);
        }
        await self.sleep(sweep + FLIP_MS + HOLD_MS);
      }
      if (!current()) break;
      // Instant, invisible snap back to 0 for every row on the sweep — no
      // stagger and no wait of its own; it rides the tail end of step 5's
      // hold instead of being a separate, shorter beat in the loop.
      for (var j = 0; j < n; j++) setBoardRowStep(j, 0, true);
      if (hasLive) {
        bridge.setStep(0, true);
        if (typeof self.forceUpdate === 'function') self.forceUpdate();
      }
    }
  };

  var orig = window.SATComponent.prototype.renderVals;
  window.SATComponent.prototype.renderVals = function () {
    var out = orig.call(this);
    if (Array.isArray(out.boardRows)) {
      out.boardRows = out.boardRows.map(function (r) {
        if (r.flipTransition === 'none') return r; // the instant reset snap — leave untouched
        return Object.assign({}, r, { flipTransition: 'transform ' + (FLIP_MS / 1000) + 's cubic-bezier(.4,0,.2,1)' });
      });
    }
    return out;
  };

  // See the top-of-file note: componentDidMount's own call to
  // startBoardWave() can bind to whichever function was on the prototype
  // at that one instant, and a script load order this file can't fully
  // control decides which one that is. Rather than depend on winning that
  // race, force the issue directly once the app exists: call this file's
  // own replacement fresh from right here. That call's own first line
  // permanently disables the flag any stale/foreign loop needs to keep
  // acting, so there is only ever one loop actually touching the board
  // from this point on, however many attempts came before it.
  function takeOverBoardWave() {
    var app = window.SATApp;
    if (!app) { setTimeout(takeOverBoardWave, 50); return; }
    if (app.__satBoardWaveTakenOver) return;
    app.__satBoardWaveTakenOver = true;
    app.startBoardWave();
  }
  takeOverBoardWave();
})();
